import axios from 'axios';
import { debug } from '@/utils/debug';
import { SESSION_KEYS } from '@/config/constants';
import { optimizationApi } from '@/services/api/optimization';
import { queryClient } from '@/providers/QueryProvider';
import { useAccountSettingsStore } from '@/store/accountSettingsStore';
import { useCampaignSettingsGoogleAdsStore } from '@/store/campaignSettingsGoogleAdsStore';
import { useCampaignSettingsMetaAdsStore } from '@/store/campaignSettingsMetaAdsStore';
import type { AppConfiguration } from '@/types/api';

/**
 * True for axios errors with HTTP status 404. Same pattern used in
 * `pages/Legal/MetaDeletionStatus`. Centralized here so the brittle
 * `error.message.includes('404')` predicate (theme #18) doesn't leak back.
 */
const is404 = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

const APP_CONFIG_QUERY_KEY = (memberUuid: string): readonly ['app-config', string] =>
  ['app-config', memberUuid] as const;

// A participating store exposes a slice of the persisted app_config payload,
// hydrates from a loaded config, and marks itself as saved once the network
// write succeeds. Both Zustand stores and any other provider that quacks like
// this shape can participate. `existing` is the most recently fetched config
// from the server (or null on the first save) so providers that need to merge
// with prior payload state (e.g. the Google store's campaign_list) can.
//
// `preSave` is an optional hook for platform-specific side effects that need
// to fire BEFORE the app_config write — e.g. Google has dedicated
// `optimizationApi.updateManualBudgets` / `updateManualNames` endpoints
// that flush per-campaign edits separately from the slice POST. Platforms
// without such endpoints simply don't implement it. The orchestrator awaits
// each provider's preSave sequentially so failures abort the save.
export interface AppConfigSliceProvider {
  getState(): {
    toConfigSlice(
      existing?: AppConfiguration['configuration'] | null,
    ): Partial<AppConfiguration['configuration']>;
    hydrateFromConfig(config: AppConfiguration['configuration'] | undefined | null): void;
    markSaved(): void;
    preSave?(): Promise<void>;
  };
}

// The default registry. Order matters for slice merge: later providers win
// on overlapping non-campaign_list keys; campaign_list is concatenated
// (each platform contributes the entries it owns). Account store is last
// so its account-level fields are canonical.
const defaultProviders: AppConfigSliceProvider[] = [
  useCampaignSettingsGoogleAdsStore,
  useCampaignSettingsMetaAdsStore,
  useAccountSettingsStore,
];

const readMemberUuid = (): string | null => localStorage.getItem(SESSION_KEYS.MEMBER_UUID);

const readExistingConfig = async (memberUuid: string): Promise<AppConfiguration | null> => {
  try {
    // `useAppConfigQuery` caches `null` (not undefined) on a 404 — that's a
    // valid sentinel meaning "we already know this member has no saved
    // config." Distinguish `undefined` (not cached) from `null` (cached as
    // no-config) so a cached null skips the network entirely and avoids an
    // extra 404 on every save.
    const cached = queryClient.getQueryData<AppConfiguration | null>(APP_CONFIG_QUERY_KEY(memberUuid));
    if (cached !== undefined) {
      debug.log(`appConfigService: using cached existing config for merge (cached: ${Boolean(cached)})`);
      return cached;
    }
  } catch {
    // Fall through to API fetch.
  }
  try {
    return await optimizationApi.loadConfiguration(memberUuid);
  } catch (error) {
    // Only treat 404 as "new user — no existing config to merge with". Any
    // other status (5xx, 403, network) MUST abort the save — proceeding
    // would treat real user data as empty, and the slice merge would then
    // POST a configuration with whatever the slices produce, potentially
    // wiping fields not owned by any registered slice (e.g. account_extensions,
    // landing_pages, ads_group_list). Re-throw so saveAppConfig surfaces the
    // failure to the user instead of silently destroying state.
    if (is404(error)) {
      debug.log('appConfigService: no existing config (new user / 404)');
      return null;
    }
    debug.error('appConfigService: read-existing failed; aborting save to avoid data loss', error);
    throw error;
  }
};

export interface SaveAppConfigOptions {
  providers?: AppConfigSliceProvider[];
}

export interface LoadAppConfigOptions {
  providers?: AppConfigSliceProvider[];
}

export const saveAppConfig = async ({ providers = defaultProviders }: SaveAppConfigOptions = {}): Promise<void> => {
  const memberUuid = readMemberUuid();
  if (!memberUuid) {
    throw new Error('Member UUID not found. Please log in again.');
  }

  // Run per-platform pre-save side effects first (e.g. Google's dedicated
  // updateManualBudgets / updateManualNames endpoints). Sequential so a
  // failure aborts the slice POST.
  for (const p of providers) {
    const state = p.getState();
    if (state.preSave) await state.preSave();
  }

  const existing = await readExistingConfig(memberUuid);
  const slices = providers.map((p) => p.getState().toConfigSlice(existing?.configuration));

  // campaign_list is the one key that doesn't follow "later slice wins"
  // merge semantics. Each platform's slice (Google, Meta, ...) contributes
  // the entries it owns; we concatenate. Existing entries whose
  // (platform_source, campaign_id) is NOT covered by any slice's
  // contributions get passed through verbatim — protects against silent
  // data loss for entries stamped with a platform_source no registered
  // provider handles (future platforms; backend-introduced stamps; legacy
  // values). Google + Meta slice builders already pass through their
  // own platform's existing entries, so this guard catches only the
  // unknown-platform case in practice.
  //
  // Default for missing platform_source: 'Google Ads' — matches the
  // backend convention used everywhere else in this codebase.
  const sliceCampaignLists = slices
    .map((s) => s.campaign_list)
    .filter((list): list is NonNullable<AppConfiguration['configuration']['campaign_list']> =>
      Array.isArray(list),
    );

  let mergedCampaignList: AppConfiguration['configuration']['campaign_list'];
  if (sliceCampaignLists.length === 0) {
    mergedCampaignList = existing?.configuration?.campaign_list ?? [];
  } else {
    const slicesFlat = sliceCampaignLists.flat();
    const stamp = (entry: { campaign_id: string | number; platform_source?: string }): string => {
      const ps = entry.platform_source ?? 'Google Ads';
      return `${ps}\0${String(entry.campaign_id)}`;
    };
    const claimedKeys = new Set(slicesFlat.map((e) => stamp(e)));
    const passthroughEntries = (existing?.configuration?.campaign_list ?? []).filter(
      (entry) => !claimedKeys.has(stamp(entry)),
    );
    mergedCampaignList = [...slicesFlat, ...passthroughEntries];
  }

  // Spread each slice's top-level keys; per-slice campaign_list contributions
  // get overwritten below by mergedCampaignList (the concatenation result).
  const mergedConfiguration = slices.reduce<Record<string, unknown>>(
    (acc, slice) => ({ ...acc, ...slice }),
    { ...(existing?.configuration ?? {}) },
  );
  mergedConfiguration.campaign_list = mergedCampaignList;

  const payload: AppConfiguration = {
    configuration: mergedConfiguration as AppConfiguration['configuration'],
  };

  await optimizationApi.saveConfiguration(payload);

  // Refresh the RQ cache so the next loadAppConfig sees the freshly-saved
  // payload. (The pre-orchestrator save path didn't do this — the next
  // load would read stale cache. Fixing it here removes the regression.)
  try {
    queryClient.setQueryData(APP_CONFIG_QUERY_KEY(memberUuid), payload);
  } catch {
    debug.log('appConfigService: failed to refresh RQ cache (non-fatal)');
  }

  providers.forEach((p) => p.getState().markSaved());
  debug.log('appConfigService: save complete');
};

export const loadAppConfig = async ({ providers = defaultProviders }: LoadAppConfigOptions = {}): Promise<void> => {
  const memberUuid = readMemberUuid();
  if (!memberUuid) {
    debug.log('appConfigService: no member uuid; skipping load');
    return;
  }

  // `useAppConfigQuery` caches `null` on 404 — treat that as "already know
  // this member has no saved config" and skip the network entirely.
  // `undefined` from getQueryData means "not cached"; `null` means "cached
  // as no-config." Without this distinction, every save fires an extra
  // loadConfiguration call that 404s.
  let response: AppConfiguration | null = null;
  let cacheHit = false;
  try {
    const cached = queryClient.getQueryData<AppConfiguration | null>(APP_CONFIG_QUERY_KEY(memberUuid));
    if (cached !== undefined) {
      response = cached;
      cacheHit = true;
    }
  } catch {
    // Fall through to API fetch.
  }
  if (!cacheHit) {
    try {
      response = await optimizationApi.loadConfiguration(memberUuid);
    } catch (error) {
      if (is404(error)) {
        debug.log('appConfigService: no saved config (new user) — hydrating providers with defaults');
        // Fall through: response stays null, hydrateFromConfig(undefined)
        // runs below so stores establish baselines from their defaults.
        // Without this, accountSettingsStore.originalSettings stays null
        // and setters can never flag dirty state → SaveChangesBar never
        // shows account-level changes for brand-new users.
      } else {
        debug.error('appConfigService: load failed', error);
        return;
      }
    }
  }

  // Hydrate every provider, even when response is null/missing. Providers
  // that need an originalSettings baseline (e.g. accountSettingsStore) will
  // seed from their defaults; providers that only act on present data (e.g.
  // googleStore.hydrateFromConfig early-returns when campaign_list is
  // missing) safely no-op.
  const config = response?.configuration;
  providers.forEach((p) => p.getState().hydrateFromConfig(config));
  debug.log(`appConfigService: hydrated providers (config present: ${Boolean(config)})`);
};

export const appConfigService = { saveAppConfig, loadAppConfig };
